import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Lock, ArrowRight, Eye, EyeOff } from "lucide-react";

const ACCESS_CODE = (process.env.REACT_APP_DEMO_ACCESS_CODE || "colosseum").trim();
const STORAGE_KEY = "cl_demo_access";
const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

export default function AccessGate({ children }) {
  const [ok, setOk] = useState(false);
  const [checked, setChecked] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [show, setShow] = useState(false);

  useEffect(() => {
    // A shared magic link (?key=CODE) unlocks instantly, then the param is stripped.
    const params = new URLSearchParams(window.location.search);
    const key = params.get("key");
    if (key && norm(key) === norm(ACCESS_CODE)) {
      localStorage.setItem(STORAGE_KEY, norm(ACCESS_CODE));
      params.delete("key");
      const qs = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash);
    }
    setOk(localStorage.getItem(STORAGE_KEY) === norm(ACCESS_CODE));
    setChecked(true);
  }, []);

  if (!checked) return null;
  if (ok) return children;

  const submit = (e) => {
    e.preventDefault();
    if (norm(code) === norm(ACCESS_CODE)) {
      localStorage.setItem(STORAGE_KEY, norm(ACCESS_CODE));
      setOk(true);
    } else {
      setError("That access code isn't right. Please check the link or code you were sent.");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0E131F] p-6" data-testid="access-gate">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-sm rounded-2xl border border-[#2A3550] bg-[#171E2E] p-8"
      >
        <div className="flex items-center gap-2.5">
          <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-[#1E2638] border border-[#2A3550]">
            <ShieldCheck className="h-5 w-5 text-[#38BDF8]" />
          </div>
          <div className="leading-tight">
            <span className="block font-display font-semibold tracking-tight text-white">Colosseum Legacy</span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#38BDF8] font-semibold">Revenue Assurance</span>
          </div>
        </div>

        <div className="mt-7 flex items-center gap-2 text-slate-300">
          <Lock className="h-4 w-4 text-slate-400" />
          <h1 className="font-display text-lg font-medium tracking-tight">Private preview</h1>
        </div>
        <p className="mt-1.5 text-sm text-slate-400">
          This is a private, invitation-only demonstration. Enter the access code you were sent to continue.
        </p>

        <form onSubmit={submit} className="mt-6 space-y-3">
          <div className="relative">
            <input
              type={show ? "text" : "password"}
              value={code}
              onChange={(e) => { setCode(e.target.value); setError(""); }}
              data-testid="access-gate-input"
              autoFocus
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              placeholder="Access code"
              className="w-full rounded-lg bg-[#1E2638] border border-[#334063] pl-4 pr-11 py-2.5 text-sm text-white focus:border-[#38BDF8] focus:outline-none transition-colors duration-200"
            />
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              data-testid="access-gate-toggle"
              aria-label={show ? "Hide access code" : "Show access code"}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-[#38BDF8] transition-colors duration-200"
            >
              {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {error && <div className="text-sm text-red-400" data-testid="access-gate-error">{error}</div>}
          <button
            type="submit"
            data-testid="access-gate-submit"
            className="w-full rounded-lg py-2.5 text-sm font-medium text-white flex items-center justify-center gap-2 bg-[#4F46E5] hover:opacity-90 transition-opacity duration-200"
          >
            Enter preview <ArrowRight className="h-4 w-4" />
          </button>
        </form>

        <p className="mt-6 text-xs text-slate-500">
          If you reached this page by accident, no further action is needed. Access is limited to invited partners.
        </p>
      </motion.div>
    </div>
  );
}
